import Config from './Config';
/**
 * Form Builder submission handling.
 *
 * `start()` delegates a single `submit` listener on `document`, which covers
 * any form that lives directly in the page's light DOM. It does NOT cover a
 * form injected by `Personalize`, though: unlike `click` (and most UI/input
 * events), `submit` is not a composed event — it never crosses a shadow
 * boundary at all, so it doesn't even reach `document`, retargeted or not.
 * (An earlier version of this fix assumed `submit` was composed and tried to
 * recover the real target via `composedPath()`; that never runs, because the
 * event doesn't arrive at `document` in the first place.) `Personalize`
 * therefore calls `listenOn(shadowRoot)` itself for every shadow root it
 * creates — see `buildNode()` in Personalize.ts — so each shadow-encapsulated
 * form gets its own listener, scoped to the same tree, where no retargeting
 * applies.
 *
 * Only forms carrying the Form Builder's marker attribute (stamped by
 * `generateFormHtml` server-side) are intercepted; any other form on the page
 * is left alone.
 *
 * Validation is entirely native (`checkValidity`/`reportValidity`): the
 * generated markup already carries `required`/`pattern`/`min`/`max` etc., so
 * there is no validation library to ship here.
 */
var FormSubmit = /** @class */ (function () {
    function FormSubmit(emit) {
        this.started = false;
        this.emit = emit;
    }
    FormSubmit.prototype.start = function () {
        if (this.started)
            return;
        this.started = true;
        this.listenOn(document);
    };
    /**
     * Wire the delegated submit listener onto an additional root. Needed for
     * every shadow root `Personalize` creates (see the class doc comment for
     * why `document` alone can't see into it).
     */
    FormSubmit.prototype.listenOn = function (root) {
        var _this = this;
        // `ShadowRoot`'s `addEventListener` isn't typed with the `submit` ->
        // `SubmitEvent` overload `Document`'s is (TS falls back to plain `Event`
        // for the `Document | ShadowRoot` union) — every native `submit` really
        // is a `SubmitEvent` regardless of which root the listener is on.
        root.addEventListener('submit', function (event) { return _this.onSubmit(event); });
    };
    FormSubmit.prototype.onSubmit = function (event) {
        // The listener is always attached to the same tree the form lives in
        // (its own shadow root, or `document` for a light-DOM form), so no
        // shadow boundary is ever crossed relative to *this* listener and
        // `event.target` is never retargeted.
        var form = event.target;
        if (!(form instanceof HTMLFormElement))
            return;
        var formId = form.getAttribute(Config.FORM_ID_ATTR);
        if (!formId)
            return;
        event.preventDefault();
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }
        var fields = this.collectFields(form);
        this.emit(formId, fields);
        this.showSuccess(form);
        form.reset();
    };
    /**
     * Flatten the form into a plain `{name: value}` object. `FormData` omits
     * unchecked checkboxes entirely, so those are read directly from the DOM
     * and always included (`false` when unchecked) — otherwise "did the
     * visitor opt out" and "this field doesn't exist on this form" would be
     * indistinguishable downstream.
     */
    FormSubmit.prototype.collectFields = function (form) {
        var fields = {};
        var data = new FormData(form);
        data.forEach(function (value, key) {
            fields[key] = value;
        });
        form
            .querySelectorAll('input[type="checkbox"]')
            .forEach(function (checkbox) {
            if (!checkbox.name)
                return;
            fields[checkbox.name] = checkbox.checked;
        });
        return fields;
    };
    FormSubmit.prototype.showSuccess = function (form) {
        // Re-submitting the same form must not stack a second message under the
        // first — reuse/replace the one from the previous submit instead. The
        // message is styled by the `.cdpf-form-success` rule of the stylesheet
        // embedded in the generated form html (core-api form-css.ts).
        var existing = form.nextElementSibling;
        if (existing && existing.classList.contains('cdpf-form-success')) {
            existing.remove();
        }
        var message = document.createElement('div');
        message.className = 'cdpf-form-success';
        message.textContent = 'Grazie!';
        form.insertAdjacentElement('afterend', message);
    };
    return FormSubmit;
}());
export default FormSubmit;
